// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsTabSection} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import * as UserCommands from '@app/features/user/commands/UserCommands';
import Users from '@app/features/user/state/Users';
import {type MentionReplyPreference, MentionReplyPreferences} from '@app/vendor/constants/UserConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';

type Value = 'no_preference' | 'prefer_mention' | 'prefer_no_mention';

function valueToFlag(value: Value): MentionReplyPreference {
	switch (value) {
		case 'prefer_mention':
			return MentionReplyPreferences.PREFER_MENTION;
		case 'prefer_no_mention':
			return MentionReplyPreferences.PREFER_NO_MENTION;
		default:
			return MentionReplyPreferences.NO_PREFERENCE;
	}
}

function flagToValue(flag: number): Value {
	if (flag === MentionReplyPreferences.PREFER_MENTION) return 'prefer_mention';
	if (flag === MentionReplyPreferences.PREFER_NO_MENTION) return 'prefer_no_mention';
	return 'no_preference';
}

export const MentionPreferenceTabContent: React.FC = observer(() => {
	const {t} = useLingui();
	const currentUser = Users.getCurrentUser();
	const current = flagToValue(currentUser?.mentionFlags ?? 0);
	const handleChange = async (value: Value) => {
		const mentionFlags = valueToFlag(value);
		const updatedUser = await UserCommands.update({mention_flags: mentionFlags});
		Users.handleUserUpdate({...updatedUser, mention_flags: mentionFlags}, {clearMissingOptionalFields: true});
	};
	return (
		<SettingsTabSection
			title={<Trans>Reply Mention Preference</Trans>}
			description={
				<Trans>
					Control whether the @ mention is on by default when others reply to you. Per-community overrides are
					configured in each community's notification settings.
				</Trans>
			}
		>
			<RadioGroup
				value={current}
				onChange={handleChange}
				aria-label={t`Reply mention preference`}
				options={[
					{
						value: 'no_preference',
						name: t`No preference`,
						desc: t`Respect the sender's intent, with no warning when they toggle the @ mention`,
					},
					{
						value: 'prefer_mention',
						name: t`Prefer @mention`,
						desc: t`Default replies to @mention you, and warn the sender if they disable it`,
					},
					{
						value: 'prefer_no_mention',
						name: t`Prefer no @mention`,
						desc: t`Default replies to omit the @mention, and warn the sender if they enable it`,
					},
				]}
			/>
		</SettingsTabSection>
	);
});
